import React, { useEffect, useState } from 'react';
import { Scale, Loader2, Save, CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '@/lib/axios';

const FIELD_GROUPS = [
  {
    title: 'Identificação da empresa',
    fields: [
      ['nome_comercial', 'Nome comercial'],
      ['denominacao_social', 'Denominação social'],
      ['nipc', 'NIPC / NIF'],
      ['morada', 'Morada da sede'],
      ['codigo_postal', 'Código postal'],
      ['localidade', 'Localidade'],
      ['conservatoria', 'Conservatória / Registo comercial'],
      ['matricula', 'Número de matrícula'],
      ['capital_social', 'Capital social'],
    ],
  },
  {
    title: 'Contactos',
    fields: [
      ['email_geral', 'Email geral'],
      ['email_privacidade', 'Email de privacidade'],
      ['telefone', 'Telefone (opcional)'],
    ],
  },
  {
    title: 'Resolução Alternativa de Litígios (RAL)',
    fields: [
      ['ral_nome', 'Nome da entidade'],
      ['ral_morada', 'Morada'],
      ['ral_telefone', 'Telefone'],
      ['ral_email', 'Email'],
      ['ral_website', 'Website'],
      ['ral_area', 'Área geográfica'],
      ['livro_reclamacoes_url', 'URL Livro de Reclamações'],
    ],
    textareas: [['ral_texto', 'Texto legal apresentado']],
  },
];

const MARCA_OPTS = [
  ['uso_comercial', 'Marca utilizada comercialmente'],
  ['pedido_pendente', 'Pedido de registo pendente'],
  ['registada', 'Marca registada'],
  ['nao_registada', 'Não registada'],
];

const REQUIRED_TO_FILL = ['nipc', 'morada', 'codigo_postal', 'localidade', 'capital_social'];

export default function MarketplaceLegalSettingsPanel() {
  const [legal, setLegal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/marketplace/legal-settings');
      setLegal(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar definições legais.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setLegal((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!legal) return;
    setSaving(true);
    setError('');
    setOk('');
    try {
      const res = await api.put('/admin/marketplace/legal-settings', legal);
      setLegal(res.data);
      setOk('Definições legais guardadas.');
      setTimeout(() => setOk(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="marketplace-admin-legal-panel">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> A carregar definições legais...
        </div>
      </section>
    );
  }

  const estado = legal?.estado || { completo: false, campos_em_falta: [] };
  const pending = estado.campos_em_falta?.length
    ? estado.campos_em_falta.map((c) => c.label)
    : REQUIRED_TO_FILL.filter((k) => !legal?.[k]?.trim());

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="marketplace-admin-legal-panel">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Scale className="h-5 w-5 text-emerald-700" />
          <div>
            <h2 className="text-base font-bold text-slate-900">Identificação legal</h2>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
              Preenchimento obrigatório antes do lançamento comercial
            </p>
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-emerald-700 disabled:opacity-60"
          data-testid="legal-save-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Guardar
        </button>
      </div>

      {pending.length === 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800" data-testid="legal-ready-warning">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Todos os campos obrigatórios estão preenchidos. Continua a ser necessária a validação da advogada
            antes do lançamento comercial — esta verificação é apenas técnica.
          </span>
        </div>
      )}

      {pending.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="legal-pending-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span data-testid="legal-pending-text">
            <strong>Configuração jurídica incompleta ({pending.length} de {estado.total_obrigatorios || 13} campos obrigatórios em falta).</strong>{' '}
            Em falta: {pending.join(', ')}. Enquanto faltarem campos, a configuração NÃO é considerada pronta e os
            valores vazios ficam ocultos nas páginas públicas (nunca aparece "por preencher").
            Nota interna: validar com a advogada. Esta verificação é técnica e não substitui validação jurídica.
          </span>
        </div>
      )}

      {FIELD_GROUPS.map((group) => (
        <div key={group.title} className="mb-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{group.title}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {group.fields.map(([key, label]) => (
              <label key={key} className="flex flex-col text-xs font-semibold text-slate-700">
                {label}
                <input
                  className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900"
                  value={legal[key] || ''}
                  onChange={(e) => set(key, e.target.value)}
                  data-testid={`legal-field-${key}`}
                />
              </label>
            ))}
          </div>
          {(group.textareas || []).map(([key, label]) => (
            <label key={key} className="mt-3 flex flex-col text-xs font-semibold text-slate-700">
              {label}
              <textarea
                rows={2}
                className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900"
                value={legal[key] || ''}
                onChange={(e) => set(key, e.target.value)}
                data-testid={`legal-field-${key}`}
              />
            </label>
          ))}
        </div>
      ))}

      {/* Registo da marca */}
      <div className="mb-2">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Registo da marca</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col text-xs font-semibold text-slate-700">
            Estado
            <select
              className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900"
              value={legal.marca_estado || 'uso_comercial'}
              onChange={(e) => set('marca_estado', e.target.value)}
              data-testid="legal-field-marca_estado"
            >
              {MARCA_OPTS.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-xs font-semibold text-slate-700">
            Número do processo
            <input
              className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900"
              value={legal.marca_numero || ''}
              onChange={(e) => set('marca_numero', e.target.value)}
              data-testid="legal-field-marca_numero"
            />
          </label>
          <label className="flex flex-col text-xs font-semibold text-slate-700">
            Data do pedido
            <input
              className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900"
              value={legal.marca_data_pedido || ''}
              onChange={(e) => set('marca_data_pedido', e.target.value)}
              data-testid="legal-field-marca_data_pedido"
            />
          </label>
          <label className="flex flex-col text-xs font-semibold text-slate-700">
            Data de concessão
            <input
              className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900"
              value={legal.marca_data_concessao || ''}
              onChange={(e) => set('marca_data_concessao', e.target.value)}
              data-testid="legal-field-marca_data_concessao"
            />
          </label>
        </div>
      </div>

      {error && <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="legal-error">{error}</p>}
      {ok && (
        <p className="mt-3 inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800" data-testid="legal-success">
          <CheckCircle2 className="h-4 w-4" /> {ok}
        </p>
      )}
      {legal.legal_updated_at && (
        <p className="mt-2 text-[11px] text-slate-400">
          Última atualização: {new Date(legal.legal_updated_at).toLocaleString('pt-PT')}
          {legal.legal_updated_by ? ` · ${legal.legal_updated_by}` : ''}
        </p>
      )}
    </section>
  );
}
